import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n, formatRelative } from "@/i18n";

export function RefreshButton({ onRefreshed }) {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const handle = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/refresh");
      if (data.throttled) {
        toast.info(t("feed.upToDate"));
      } else {
        toast.success(`${t("feed.updated")} — ${data.new_articles}`);
      }
      setLastRefresh(data.last_refresh || new Date().toISOString());
      await qc.invalidateQueries();
      onRefreshed?.();
    } catch (e) {
      const status = e.response?.status;
      toast.error(status === 429 ? t("feed.refreshing") : t("feed.failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-2">
      <button
        data-testid="refresh-news-btn"
        onClick={handle}
        disabled={loading}
        aria-busy={loading}
        aria-label={t("feed.refresh")}
        className="inline-flex items-center gap-2 bg-violet-600 text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-violet-700 transition-colors disabled:opacity-60"
      >
        <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
        {loading ? t("feed.refreshing") : t("feed.refresh")}
      </button>
      {lastRefresh && (
        <span data-testid="last-refresh-time" className="text-[11px] text-muted-foreground">
          {t("feed.lastUpdated")}: {formatRelative(lastRefresh, lang)}
        </span>
      )}
    </div>
  );
}
